<template>
  <div class="comment mb-3 bg-white"
       :class="{ collapsible, fullScreenMode }"
       v-if="hasEnoughData">
    <PatientCommentTeaserTop v-if="!collapsible"
                             :editMode="editMode"
                             :canBeEdited="canBeEdited"
                             @deleteComment="deleteComment"
                             @editComment="editComment"
                             @togglePin="togglePin"
                             :comment="comment"/>
    <div v-if="text || (comment.scans && comment.scans.length) || (comment.documents && comment.documents.length) || commentType === 'call' || editMode"
         class="comment-content">
      <div class="comment-header d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <component :is="icon"
                     class="mr-2"/>
          <span class="mr-2"
                @click="titleClicked"
                :class="{
                'text-underlined pointer': ['record','treatment_plan'].includes(commentType),
                'pointer': titleClickable
              }"
                v-html="title"/>
          <span v-if="comment.variables && comment.variables.status"
                :class="`badge badge-${comment.variables.status}`">
          {{ $t(comment.variables.status) }}
        </span>
        </div>
        <div class="d-flex gap-0"
             v-if="collapsible">
          <a class="pointer d-flex align-items-center p-2"
             @click="togglePin"
             :title="comment.pinned ? $t('unpin') : $t('pin')"
             v-b-tooltip.hover>
            <PinBoldSvg class="svg-size-lg svg-stroke-gray2"/>
          </a>
          <a class="pointer d-flex align-items-center p-2"
             @click="collapsed = !collapsed"
             :title="collapsed ? $t('expand') : $t('collapse')"
             v-b-tooltip.hover>
            <EyeClosedSvg class="svg-size-lg svg-gray2" v-if="collapsed"/>
            <EyeSvg class="svg-size-lg svg-gray2" v-if="!collapsed"/>
          </a>
          <FullScreenBtn @toggle-mode="toggleFullMode"
                         class="full-screen-btn"
                         :fullScreenOn="fullScreenMode"/>
        </div>
      </div>

      <Transition :css="false"
                  @enter="onFormulaEnter"
                  @leave="onFormulaLeave">
        <div class="comment-block"
             v-if="!collapsed">

          <PatientCommentTeaserTop v-if="collapsible"
                                   class="mb-2"
                                   :editMode="editMode"
                                   :canBeEdited="canBeEdited"
                                   @deleteComment="deleteComment"
                                   @editComment="editComment"
                                   @togglePin="togglePin"
                                   :comment="comment"/>

          <div v-if="editMode" class="pb-3 pt-2">
            <!--          <AutosizeTextArea ref="change"-->
            <!--                            v-model="changedText"-->
            <!--                            :showlabel="false"/>-->
            <RichTextArea
                ref="change"
                :showlabel="false"
                v-model="changedText"/>
            <div class="text-right">
              <button class="btn btn-themed btn-themed-squared btn-themed-outline"
                      @click="cancelEdit">
                {{ $t('cancel') }}
              </button>
              <button class="btn btn-themed btn-themed-squared ml-3"
                      @click="saveComment">
                {{ $t('save') }}
              </button>
            </div>
          </div>
          <TaskCommentText v-else-if="commentType === 'task'"
                           :text="text"
                           :task="comment.task"/>
          <LeadCommentText v-else-if="commentType === 'lead'"
                           :text="text"
                           :lead="comment.lead"/>
          <div v-else v-html="text"></div>
          <div v-if="comment.scans && comment.scans.length">
            <div v-if="showImages || forceShowImage" class="mt-3">
              <div v-for="scan in comment.scans"
                   :key="`scan-${scan.id}`"
                   class="d-inline-block mb-3">
                <expandable-image class="img-preview"
                                  alt="image"
                                  :src="scan.url ? scan.url : scan.image"/>
              </div>
            </div>
            <div v-else class="text-muted small">
              {{ $t('image_hidden') }}
              <a class="ml-1 pointer" @click="forceShowImage = true"><EyeSvg class="svg-light svg-size-md"/></a>
            </div>
          </div>
          <div v-if="comment.documents && comment.documents.length"
               :class="{ 'mt-3': !!text }">
            <div v-for="(doc, docInd) in comment.documents"
                 :key="`doc-${doc.id}`"
                 :class="{ 'mt-3': docInd > 0 }"
                 class="d-inline-block">
              <a :href="documentUrl(doc)" target="_blank">
                {{ doc.original }}
              </a>
            </div>
          </div>
          <div v-if="commentType === 'call'">
            <a class="text-underlined pointer"
               @click="linkToCall"
               v-if="!recordLink">
              {{ $t('listen') }}
            </a>
            <audio controls
                   v-if="recordLink"
                   class="mt-1"
                   autoplay>
              <source :src="recordLink" type="audio/mpeg">
            </audio>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup>
import PinBoldSvg from "@/assets/svg-vue/general/pin-bold.svg.vue"
import EyeClosedSvg from '@/assets/svg-vue/form/closed_eye.svg'
import CommentSvg from '@/assets/svg-vue/patients/comment_comment.svg'
import NotificationSvg from '@/assets/svg-vue/patients/comment_notification.svg'
import PaymentSvg from '@/assets/svg-vue/patients/comment_payment.svg'
import TaskSvg from '@/assets/svg-vue/patients/comment_task.svg'
import TreatmentPlanSvg from '@/assets/svg-vue/patients/comment_treatment_plan.svg'
import VisitSvg from '@/assets/svg-vue/patients/comment_visit.svg'
import BonusSvg from '@/assets/svg-vue/patients/comment_star.svg'
import PhoneSvg from '@/assets/svg-vue/patients/phone.svg'
import TimeSvg from '@/assets/svg-vue/calendar/clock3.svg'
import PatientsSvg from '@/assets/svg-vue/header/user_lenta.svg'
import EyeSvg from '@/assets/svg-vue/form/eye.svg'
import { BinotelService } from "@/services/api/binotel.api.service"
import { EloquentService } from "@/services/api/eloquent.api.service"
import JwtService from "@/services/jwt.service"
import { computed, getCurrentInstance, nextTick, onBeforeMount, ref } from "vue"
import { useStore } from "vuex"
import { useRoute, useRouter } from "vue-router"
import moment from "moment"
import {formatCurrency, formatDateTime, formatMinutes} from "@/extensions/filters/filters"
import {appendFormdata, copyObject} from "@/extensions/prototypes/prototypes"
import TaskCommentText from "@/components/parts/patients/comment/TaskCommentText.vue"
import LeadCommentText from "@/components/parts/patients/comment/LeadCommentText.vue"
import RichTextArea from "@/components/form/RichTextArea.vue"
import FullScreenBtn from "@/components/parts/general/FullScreenBtn.vue"
import PatientCommentTeaserTop from "@/components/parts/patients/PatientCommentTeaserTop.vue"
import {onElementHideCollapse, onElementShowExpand} from "@/services/animation.service"

const props = defineProps({
  comment: Object,
  showImages: {
    type: Boolean,
    default: true
  },
  collapsible: {
    type: Boolean,
    default: false
  },
  initialCollapseOpened: {
    type: Boolean,
    default: false
  }
})
const emit = defineEmits(['getComments', 'repinnedComment'])

const store = useStore()
const route = useRoute()
const router = useRouter()

let i18n = null
let bvModal = null
let noty = null

const dict = {
  manual: { icon: CommentSvg, title: 'comment' },
  payment: { icon: PaymentSvg, title: 'payment' },
  advance: { icon: PaymentSvg, title: 'payment' },
  notification: { icon: NotificationSvg, title: 'mailing' },
  treatment_plan: { icon: TreatmentPlanSvg, title: 'treatment_plan' },
  task: { icon: TaskSvg, title: 'task' },
  lead: { icon: TaskSvg, title: 'lead' },
  record: { icon: VisitSvg, title: 'visit' },
  bonus: { icon: BonusSvg, title: 'bonus' },
  call: { icon: PhoneSvg, title: 'call' },
  visit_timing: { icon: TimeSvg, title: 'visit_time' },
  patient: { icon: PatientsSvg, title: 'patient' },
}

const recordLink = ref(null)
const editMode = ref(false)
const changedText = ref('')
const forceShowImage = ref(false)
const collapsed = ref(props.collapsible && !props.initialCollapseOpened)
const fullScreenMode = ref(false)
const change = ref(null)

const clinic = computed(() => store.state.auth.clinic)
const user = computed(() => store.state.auth.user)
const getPaymentById = computed(() => store.getters.getPaymentById)

const icon = computed(() => dict[props.comment.comment_type].icon)
const commentType = computed(() => props.comment.comment_type)

const hasEnoughData = computed(() => {
  if(!props.comment) return false
  if(commentType.value === 'payment' && !props.comment.payment) return false
  if(commentType.value === 'advance' && !props.comment.advance) return false
  if(commentType.value === 'record' && !props.comment.record) return false
  if(commentType.value === 'task' && !props.comment.task) return false
  if(commentType.value === 'treatment_plan' && !props.comment.plan) return false
  if(commentType.value === 'notification' && !props.comment.notification) return false
  if(commentType.value === 'bonus' && !props.comment.payment && !props.comment.bonus_transaction) return false
  if(commentType.value === 'comment' && !props.comment.variables?.id) return false
  if(commentType.value === 'visit_timing' && !props.comment.record?.id) return false
  if(commentType.value === 'lead' && !props.comment.lead?.title) return false
  // if(commentType.value === 'patient' && !props.comment.lead?.title) return false
  return true
})

const title = computed(() => {
  const pretitle = i18n.t(dict[commentType.value].title)
  switch(commentType.value) {
    case 'record':
      if(props.comment.record) {
        return `${pretitle} ${props.comment.record.time}`
      }
      break;
    case 'payment':
      if(props.comment.payment) {
        return `${pretitle} ${props.comment.payment.time}`
      }
      break;
    case 'advance':
      if(props.comment.advance) {
        return `${pretitle} ${props.comment.advance.time}`
      }
      break;
    case 'notification':
      return pretitle
    case 'task':
      return pretitle
    case 'lead':
      if(props.comment.lead) {
        return `${props.comment.lead.title}`
      }
      return pretitle
    case 'treatment_plan':
      if(props.comment.plan) {
        return `${pretitle} "${props.comment.plan.title}"`
      }
      break;
    case 'bonus':
      return pretitle
    case 'call':
      return i18n.t('call_noun')
    case 'patient':
      return pretitle
    case 'visit_timing':
      if(props.comment.action === 'close') return i18n.t('visit_finished')
      return i18n.t('visit_started')
    case 'manual':
      if(props.comment.task) return `${pretitle} | <span class="text-primary">${i18n.t('task')} ${props.comment.task.title}</span>`
      if(props.comment.lead) return `${pretitle} | <span class="text-primary">${props.comment.lead.title}</span>`
      if(props.comment.canban) return `${pretitle} | <span class="text-primary">${i18n.t('funnel')}</span>`
  }
  return pretitle
})

const text = computed(() => {
  switch(commentType.value) {
    case 'record':
      return generateTextForVisit()
    case 'payment':
      return generateTextForPayment()
    case 'advance':
      return generateTextForAdvance()
    case 'notification':
      return generateTextForNotification()
    case 'task':
      return generateTextForTask()
    case 'lead':
      return generateTextForLead()
    case 'treatment_plan':
      return generateTextForTreatmentPlan()
    case 'bonus':
      return generateTextForBonus()
    case 'call':
      return generateTextForCall()
    case 'visit_timing':
      return generateTextForVisitTiming()
    case 'patient':
      return generateTextForPatient()
  }
  return props.comment.text
})

const isMine = computed(() => user.value.id === props.comment.author?.id)
const canBeEdited = computed(() => commentType.value === 'manual' && isMine.value)
const titleClickable = computed(() => {
  if(commentType.value === 'manual') {
    if(props.comment?.lead?.id || props.comment?.task?.id || props.comment?.canban?.id) return true
  }
  if(commentType.value === 'record' && props.comment.record) return true
  if(commentType.value === 'treatment_plan' && props.comment.plan) return true
  return false
})

onBeforeMount(() => {
  const instance = getCurrentInstance()
  i18n = instance?.ctx?.$i18n
  bvModal = instance?.ctx?._bv__modal
  noty = instance?.appContext?.app?.noty
})

const editComment = async () => {
  changedText.value = props.comment.text
  editMode.value = true
  await nextTick()
  change.value?.focusCustom()
}

const cancelEdit = () => {
  editMode.value = false
}

const saveComment = async () => {
  let formData = new FormData()
  formData = appendFormdata(formData, {
    text: changedText.value
  })
  props.comment.text = changedText.value
  await EloquentService.update('patient_comment', props.comment.id, formData)
  noty.info(i18n.t('success_message'))
  emit('getComments')
  editMode.value = false
}
const deleteComment = () => {
  if(confirm(i18n.t('are_you_sure_you_want_to_delete_record'))) {
    EloquentService.delete('patient_comment', props.comment.id).then(() => {
      noty.info(i18n.t('success_message'))
      emit('getComments')
    })
  }
}

const generateTextForVisit = () => {
  if(!props.comment.record) return ''
  const record = props.comment.record
  let str = `${i18n.t('visit')} ${record.time}, ${i18n.t('duration')}: ${formatMinutes(record.duration)}`
  if(record.doctor) str += `<br/>${i18n.t('doctor')}: ${record.doctor}`
  if(record.assistant) str += `<br/>${i18n.t('assistant')}: ${record.assistant}`
  if(props.comment.author) str += `<br/>${i18n.t('date_add')}: ${props.comment.author.full_name}, ${formatDateTime(record.created_at)}`
  if(record.confirmed_by) str += `<br/>${i18n.t('confirmed')}: ${record.confirmed_by}`

  let block2Str = ''
  if(record.logs.length) {
    record.logs.forEach((log, index) => {
      if(index > 0) block2Str += `<br/>`
      block2Str += `<br/>${log.readable_date}`
      block2Str += `<br/>${log.conclusion}, ${log.author}`
    })
  }
  if(props.comment.variables?.status === 'cancelled' && record.cancelled_by && record.cancelled_at) {
    block2Str += `<br/><br/>${record.cancelled_at}`
    block2Str += `<br/>${i18n.t('status')}: ${i18n.t('cancelled')}`
    block2Str += `<br/>${i18n.t('cancel_initiator')}: ${record.cancelled_by}`
    if(record.reason) block2Str += `, ${i18n.t('cancel_reason')}: ${record.reason}`
  }
  if(record.timeMovements) {
    record.timeMovements.forEach(item => {
      block2Str += `<br/>${i18n.t('postponed')}: ${moment(item.old_date).format('DD.MM.YYYY, HH:mm')} -> ${moment(item.new_date).format('DD.MM.YYYY, HH:mm')}`
      if(item.user) block2Str += `, ${item.user.full_name}`
    })
  }
  if(block2Str) {
    str += '<br/>================================='
    str += block2Str
  }
  let block3Str = ''
  if(record.diagnoses) block3Str += `<br/>${i18n.t('diagnoses')}: ${record.diagnoses}`
  if(record.plan) block3Str += `<br/>${i18n.t('treatment_plan')}: ${record.plan}`
  if(record.services) block3Str += `<br/>${i18n.t('services')}: ${record.services}`
  if(record.price) block3Str += `<br/>${i18n.t('cost')}: ${formatCurrency(record.price)}`
  if(record.description) block3Str += `<br/>${i18n.t('description')}: ${record.description}`
  if(record.source) block3Str += `<br/>${i18n.t('source')}: ${record.source}`
  if(block3Str) {
    str += '<br/>================================='
    str += block3Str
  }
  return str
}

const generateTextForPayment = () => {
  if(!props.comment.payment) return ''
  const payment = props.comment.payment
  let str = ''
  if(props.comment.variables?.deleted) {
    return `⛔ ${i18n.t('deleted')}`
  }
  if(props.comment.variables) {
    if(props.comment.variables?.old_price || props.comment.variables?.new_paid) {
      str += `${i18n.t('sum')}: <span class="crossed-out text-muted mr-2">${formatCurrency(props.comment.variables?.old_paid || 0)}</span><span>${formatCurrency(props.comment.variables?.new_paid || 0)}</span><br/>`
    }
    if(props.comment.variables?.old_forma || props.comment.variables?.new_forma) {
      str += `${i18n.t('payment_type')}: <span class="crossed-out text-muted mr-2">${(getPaymentById.value(props.comment.variables?.old_forma || 0)?.translatedTitle || '')}</span><span>${(getPaymentById.value(props.comment.variables?.new_forma || 0)?.translatedTitle || '-')}</span><br/>`
    }
    if(props.comment.variables?.old_date || props.comment.variables?.new_date) {
      str += `${i18n.t('date')}: <span class="crossed-out text-muted mr-2">${formatDateTime(props.comment.variables?.old_date)}</span><span>${formatDateTime(props.comment.variables?.new_date)}</span><br/>`
    }
  } else {
    str += `${i18n.t('payment')} ${formatCurrency(payment.sum)}`
    str += `<br/>${i18n.t('payment_type')}: ${payment.payment_type}<br/>`
  }
  if(payment.visit && !props.comment.variables) str += `${i18n.t('visit')}: #${payment.visit.id} ${i18n.t('from')} ${payment.visit.time}`
  return str
}

const generateTextForAdvance = () => {
  if(!props.comment.advance) return ''
  const advance = props.comment.advance
  if(props.comment.variables?.deleted) {
    return `⛔ ${i18n.t('deleted')}`
  }
  let str = ''
  if(props.comment.variables) {

    if(props.comment.variables?.old_date || props.comment.variables?.new_date) {
      str += `${i18n.t('date')}: <span class="crossed-out text-muted mr-2">${formatDateTime(props.comment.variables?.old_date)}</span><span>${formatDateTime(props.comment.variables?.new_date)}</span><br/>`
    }
  } else {
    str += `${i18n.t('payment')} ${formatCurrency(advance.sum)}`
    str += `<br/>${i18n.t('payment_type')}: ${advance.payment_type}`
    if(advance.sum > 0) {
      str += `<br/>${i18n.t('balance_refill')}`
    }
    if(advance.sum < 0) {
      str += `<br/>${i18n.t('balance_writeoff')}`
    }
  }
  return str
}

const generateTextForNotification = () => {
  if(!props.comment.notification) return ''
  const notification = props.comment.notification
  let str = `${i18n.t('mailing')} ${notification.sent_at}`
  if(notification.channels) str += `<br/>${i18n.t('channel')}: ${notification.channels}`
  if(notification.text) str += `<br/>${i18n.t('text')}: ${notification.text}`
  return str
}

const generateTextForTask = () => {
  if(!props.comment.task) return ''
  const task = props.comment.task
  let str = `${i18n.t('task')}: ${i18n.t(task.type)}`
  if(task.description) str += `<br/>${i18n.t('description')}: ${task.description}`
  if(task.responsible) str += `<br/>${i18n.t('responsible')}: ${task.responsible}`
  if(task.deadline) str += `<br/>${i18n.t('deadline')}: ${task.deadline}`
  return str
}

const generateTextForLead = () => {
  if(!props.comment.lead) return ''
  const lead = props.comment.lead
  let str = `${i18n.t('channel')}: ${lead.channel}`
  if(lead.comment) str += `<br/>${i18n.t('comment')}: ${lead.comment}`
  return str
}

const generateTextForTreatmentPlan = () => {
  if(!props.comment.plan) return ''
  const plan = props.comment.plan
  const visits = plan.visits
  let str = ''
  visits.forEach(visit => {
    if(str) str += '<br/>'
    str += `${visit.title}, ${i18n.t('status')}: ${visit.status}.`
  })
  if(visits.length === 0) {
    str = i18n.t('no_complexes')
  }
  return str
}

const generateTextForBonus = () => {
  let str = ''
  if(props.comment.payment) {
    const payment = props.comment.payment
    if(payment.sum) { // means paid with bonuses
      str += `${i18n.t('bonus_payment')}: ${formatCurrency(payment.sum)}`
    }
    if(props.comment.payment.accrued) {
      if(str) str += '<br/>'
      str += `${i18n.t('bonus_accrued')}: ${formatCurrency(payment.accrued)}`
    }
    str += `<br/>${i18n.t('payment')} ${payment.time}`
    if(payment.visit) str += `<br/>${i18n.t('visit')}: #${payment.visit.id} ${i18n.t('from')} ${payment.visit.time}`
  }
  if(props.comment.bonus_transaction) {
    str += `${i18n.t('bonus_adjustment')}: `
    if(props.comment.bonus_transaction.sum < 0) {
      str += i18n.t('write_off').toLowerCase()
    } else {
      str += i18n.t('accrual').toLowerCase()
    }
    str += ` ${formatCurrency(props.comment.bonus_transaction.sum)}`
  }
  return str
}

const generateTextForCall = () => {
  return ''
}

const generateTextForVisitTiming = () => {
  let str = ''
  if(props.comment.action === 'open') {
    str += `${i18n.t('visit_started')}: ${props.comment.date}`
  }
  if(props.comment.action === 'close') {
    str += `${i18n.t('visit_finished')}: ${props.comment.date}`
  }
  str += `<br/>${i18n.t('visit')} ${props.comment.record.time}`
  // if(record.doctor) str += `<br/>${i18n.t('doctor')}: ${record.doctor}`
  return str
}

const generateTextForPatient = () => {
  let str = props.comment.text
  if(props.comment.author) str += `<br/>${i18n.t('by')}: ${props.comment.author.full_name}`
  if(props.comment.patient?.title) str += `<br/>${i18n.t('with_patient')}: ${props.comment.patient.title}`
  return str
}

const documentUrl = (doc) => {
  let link = JwtService.getPrintPrelink(clinic.value)
  return link+'/client/document/'+doc.id
}

const titleClicked = async () => {
  if(commentType.value === 'manual') {
    if(props.comment?.lead?.id) {
      await router.push({
        name: 'CRMLeadPage',
        params: { id: props.comment.lead.id }
      })
      return
    }
    if(props.comment?.task?.id) {
      const task = await EloquentService.show('task', props.comment.task.id)
      if(!task?.data) return
      store.commit('setTask', copyObject(task.data))
      bvModal.show('task-show-modal')
      return
    }
    if(props.comment?.canban?.id) {
      await router.push({
        name: 'CRMFunnelPatientPage',
        params: { canbanId: props.comment.canban.id }
      })
      return
    }
  }
  if(commentType.value === 'record' && props.comment.record) {
    router.push({ name: 'PatientVisitPage', params: {
        id: route.params.id,
        visitid: props.comment.record.id,
        page: 'teeth'
      }
    }).catch(()=>{})
  }
  if(commentType.value === 'treatment_plan' && props.comment.plan) {
    router.push({ name: 'PatientTreatmentPlan', params: {
        id: route.params.id,
      }
    }).catch(()=>{})
    let res = await EloquentService.dataTable('plan_treatment', 1, { condition: { planning_id: props.comment.plan.id }})
    if(res.data && res.data.result.data.length) {
      const plan = res.data.result.data[0]
      await router.push({
        name: 'TreatmentPlanFullCard',
        params: {
          patientId: plan.client_id,
          planId: plan.planning_id,
        }
      })
    }
  }
}

const linkToCall = async () => {
  if(props.comment.variables?.record_link) {
    recordLink.value = props.comment.variables.record_link
    return
  }
  const id = props.comment.variables?.id
  if(!id) return
  const vendor = props.comment.variables?.vendor
  let res = null
  if(vendor === 'uis') {
    res = await BinotelService.getUISCallRecording(props.comment.id)
  } else {
    res = await BinotelService.getCallRecording(id)
  }
  if(res.data?.url) {
    recordLink.value = res.data.url
  } else {
    noty.error(i18n.t('record_not_available'))
  }
}

const togglePin = async () => {
  let formData = new FormData()
  const pinned = props.comment.pinned ? 0 : 1
  formData.append('pinned', pinned)
  await EloquentService.update('patient_comment', props.comment.id, formData)
  emit('getComments')
  emit('repinnedComment', pinned ? props.comment : null)
}

const toggleFullMode = () => {
  if(!fullScreenMode.value) {
    collapsed.value = false
  }
  fullScreenMode.value = !fullScreenMode.value
}

const onFormulaEnter = (el, done) => {
  onElementShowExpand(el, done)
}

const onFormulaLeave = (el, done) => {
  onElementHideCollapse(el, done)
}
</script>

<style scoped lang="scss">
.comment-content {
  border: 1px solid #B5C6D8;
  border-radius: 5px;
  overflow: hidden;
  white-space: pre-line;
  .comment-header {
    background: #F2F6FA;
    padding: 5px 16px;
    color: #495A6B;
    font-weight: 600;
    .badge {
      &-not_confirmed, &-postponed, &-planned, &-preliminary, &-inprogress, &-blocked, &-stopped {
        background: #B5C6D8;
        color: #fff;
      }
      &-confirmed, &-came, &-active {
        background: #C6F3B6;
        color: #398320;
      }
      &-in_chair, &-done, &-treatment_complete, &-sent, &-paid {
        background: #71CF50;
        color: #fff;
      }
      &-did_not_come, &-cancelled, &-debt, &-rejected {
        background: #F3C4C3;
        color: #AA2D29;
      }
    }
  }
  .comment-block {
    padding: 5px 16px;
    word-wrap: break-word;
  }
}
.pinned {
  color: #ADAFB8;
  font-size: 13px;
  position: relative;
  top: -1px;
}
.collapsible {
  .comment-content {
    border: 1px solid $border-new-color;
    border-radius: 12px;
    .comment-header {
      background-color: transparent;
    }
  }
}
.comment {
  &.fullScreenMode {
    padding: 15px;
  }
}
@media screen and (max-width: 768px) {
  .pinned {
    font-size: 11px;
  }
}

</style>
